
import React, { useState, useEffect, useRef } from 'react';
import { ArchiveFile, Bookmark, BookmarkCategory } from '../types';
import { PUBLIC_READINGS_URL_BASE } from '../constants';
import { getWikiArticle } from '../services/firestoreService';
import { getMockContent } from '../services/mockStorage';
import { getFallbackReadingContent } from '../services/fallbackReadings';
import WikiHeader from './WikiHeader';
import DOMPurify from 'dompurify';
import { uploadMedia, fileToBase64, getResizedImageUrl, getSafeDownloadUrl } from '../services/storageService';
import { notifyTTSStarted, subscribeOtherTabTTS } from '../services/ttsChannel';

interface ArticleContentProps {
  content: string;
}

const ArticleContent = React.memo(React.forwardRef<HTMLDivElement, ArticleContentProps>(({ content }, ref) => {
    return (
        <div 
            ref={ref}
            className="prose prose-stone dark:prose-invert max-w-none text-stone-800 dark:text-stone-200 leading-relaxed font-serif"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
        />
    );
}));

interface ReadingViewProps {
  file: ArchiveFile;
  activePage: string;
  onBack: () => void;
  isEditorMode: boolean;
  onSave: (id: string, data: any, isAutoSave: boolean) => Promise<boolean>;
  allFiles: ArchiveFile[];
  highlightText: string;
  onMenuClick: () => void;
  isSidebarOpen: boolean;
  sidebar?: React.ReactNode;
  showSearch?: boolean;
  searchQuery?: string;
  onSearchChange?: (val: string) => void;
  onOpenBookmarkDialog: (title: string, fileId: string, fileName: string, paragraphId?: string, paragraphText?: string) => void;
  onDeleteBookmark: (bookmarkId: string) => void;
  bookmarks: Bookmark[];
  bookmarkCategories: BookmarkCategory[];
  breadcrumbRootLabel?: string;
}

const ReadingView: React.FC<ReadingViewProps> = ({
  file,
  activePage,
  onBack,
  highlightText,
  onMenuClick,
  isSidebarOpen,
  sidebar,
  showSearch,
  searchQuery,
  onSearchChange,
  onOpenBookmarkDialog,
  onDeleteBookmark,
  bookmarks,
  bookmarkCategories,
  breadcrumbRootLabel
}) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const [permanentlyHighlightedEl, setPermanentlyHighlightedEl] = useState<HTMLElement | null>(null);

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const stopSpeakingRef = useRef(false);
  const [isTTSSettingsOpen, setIsTTSSettingsOpen] = useState(false);
  const ttsSettingsRef = useRef<HTMLDivElement>(null);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState<string | null>(null);
  const [speechRate, setSpeechRate] = useState(1);
  const [hasSelection, setHasSelection] = useState(false);

  const originalHTMLsRef = useRef(new Map<HTMLElement, string>());
  const highlightedWordRef = useRef<HTMLElement | null>(null);

  const permanentHTMLsRef = useRef<Map<HTMLElement, string>>(new Map());

  const restorePermanentHTMLs = () => {
    permanentHTMLsRef.current.forEach((html, el) => {
        el.innerHTML = html;
        el.classList.remove('permanent-highlight', 'has-permanent-highlight');
    });
    permanentHTMLsRef.current.clear();
  };

  useEffect(() => {
    const updateVoices = () => setVoices(window.speechSynthesis.getVoices());
    updateVoices();
    window.speechSynthesis.onvoiceschanged = updateVoices;
    
    const handleClickOutside = (event: MouseEvent) => {
        if (ttsSettingsRef.current && !ttsSettingsRef.current.contains(event.target as Node)) {
            setIsTTSSettingsOpen(false);
        }
    };
    document.addEventListener('mousedown', handleClickOutside);

    const checkSelection = () => {
        const selection = window.getSelection();
        setHasSelection(selection ? selection.toString().trim().length > 0 : false);
    };
    document.addEventListener('selectionchange', checkSelection);
    document.addEventListener('mouseup', checkSelection);

    const unsubscribeTTS = subscribeOtherTabTTS(() => {
        stopSpeaking();
    });

    return () => {
        window.speechSynthesis.onvoiceschanged = null;
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('selectionchange', checkSelection);
        document.removeEventListener('mouseup', checkSelection);
        unsubscribeTTS();
    };
  }, []);
  
  useEffect(() => {
    if (isSpeaking) {
      stopSpeaking();
    }
  }, [selectedVoiceURI, speechRate]);

  const parseCitationsInHtml = (html: string): string => {
      if (!html) return '';

      const createLink = (readingIdRaw: string, quoteRaw: string): string => {
          const readingId = readingIdRaw.trim();
          const quote = quoteRaw.trim().replace(/^"|"$/g, '').replace(/^'|'$/g, '');
          let normalizedId = readingId;
          const rMatch = readingId.match(/Reading\s*(\d+)-(\d+)/i);
          if (rMatch) {
              normalizedId = `${rMatch[1].padStart(4, '0')}-${rMatch[2].padStart(4, '0')}`;
          }
          const href = `/readings/${encodeURIComponent(normalizedId)}?highlight=${encodeURIComponent(quote)}`;

          return `<a 
              href="${href}"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-800/60 transition-all font-bold text-xs mx-1 no-underline align-middle"
              title="Open Reading Reference"
          >
              <i class="fa-solid fa-book-open text-[10px]"></i>
              ${readingId}
          </a>`;
      };

      let processedHtml = html.replace(/"([^"]+?)"(\s*)\[\[([^|]+?)\s*\|\s*"\1"\]\]/g, (match, quote, space, readingId) => {
          const link = createLink(readingId, quote);
          return `"${quote}"${space}${link}`;
      });
      
      processedHtml = processedHtml.replace(/\[\[([\s\S]+?)\s*\|\s*([\s\S]+?)\]\]/g, (match, readingIdRaw, quoteRaw) => {
          const link = createLink(readingIdRaw, quoteRaw);
          const quoteText = quoteRaw.trim().replace(/^"|"$/g, '').replace(/^'|'$/g, '');
          return `<span class="italic">"${quoteText}"</span> ${link}`;
      });

      return processedHtml;
  };

  useEffect(() => {
    const loadContent = async () => {
      setLoading(true);
      try {
        if (file.path && (file.path.startsWith('Reading') || file.name.startsWith('Reading'))) {
            const encodedPath = file.path.split('/').map(s => encodeURIComponent(s)).join('/');
            const url = `${PUBLIC_READINGS_URL_BASE}${encodedPath}`;
            const res = await fetch(url);
            let html = '';
            if (res.ok) {
                html = await res.text();
            } else {
                const fallback = getFallbackReadingContent(file.path) || getFallbackReadingContent(file.id);
                if (fallback) {
                    html = fallback;
                } else {
                    throw new Error(`GCS Fetch returned status ${res.status}`);
                }
            }
            
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            const pathParts = file.path.split('/');
            pathParts.pop(); 
            const docBaseUrl = `${PUBLIC_READINGS_URL_BASE}${pathParts.join('/')}${pathParts.length > 0 ? '/' : ''}`;
            
            doc.querySelectorAll('img[src]').forEach(imgNode => {
                const img = imgNode as HTMLImageElement;
                const src = img.getAttribute('src');
                if (src && !src.startsWith('http') && !src.startsWith('data:')) {
                    try {
                        img.src = new URL(src, docBaseUrl).href;
                    } catch (e) {
                        console.error(`Invalid relative URL for image: ${src}`, e);
                    }
                }
            });
    
            doc.querySelectorAll('a[href]').forEach(linkNode => {
                const link = linkNode as HTMLAnchorElement;
                const href = link.getAttribute('href');
                if (href && !href.startsWith('http') && !href.startsWith('#') && !href.startsWith('mailto:')) {
                    try {
                       link.href = new URL(href, docBaseUrl).href;
                       link.target = '_blank'; 
                    } catch (e) {
                       console.error(`Invalid relative URL for link: ${href}`, e);
                    }
                }
            });

            let processedHtml = doc.body.innerHTML;
            
            // More comprehensive page marker removal (matches variations like 4665-3 Page 2, 0123-4  Page 5 etc.)
            const pageMarkerRegex = /\b\d+-\d+\s+Page\s+\d+\b/gi;
            
            // 1. Remove markers inside fully isolated paragraphs
            processedHtml = processedHtml.replace(new RegExp(`<p[^>]*>\\s*${pageMarkerRegex.source}\\s*</p>`, 'gi'), '');
            
            // 2. Remove markers that are just loose in the text (often with line breaks)
            processedHtml = processedHtml.replace(pageMarkerRegex, '');

            // 3. Clean up empty paragraphs that might have been left behind
            processedHtml = processedHtml.replace(/<p[^>]*>\s*(?:&nbsp;|\s)*<\/p>/gi, '');

            processedHtml = processedHtml.replace(/<p[^>]*>\s*P\s+R\s+E\s+S\s+E\s+N\s+T\s*<\/p>/gi, '<h3>PRESENT</h3>');
            processedHtml = processedHtml.replace(/<p[^>]*>\s*R\s+E\s+A\s+D\s+I\s+N\s+G\s*<\/p>/gi, '<h3 id="reading-start-header">READING</h3>');

            setContent(parseCitationsInHtml(processedHtml));
        } else {
            const data = await getWikiArticle(file.id);
            if (data && data.content) {
                setContent(parseCitationsInHtml(data.content));
            } else {
                const mock = getMockContent(file.id);
                setContent(parseCitationsInHtml(mock || file.content || "No content available for this record."));
            }
        }
      } catch (e: any) {
        console.error("Load failed", e.message || e);
        const fallback = getFallbackReadingContent(file.path) || getFallbackReadingContent(file.id);
        if (fallback) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(fallback, 'text/html');
            let processedHtml = doc.body.innerHTML;
            const pageMarkerRegex = /\b\d+-\d+\s+Page\s+\d+\b/gi;
            processedHtml = processedHtml.replace(new RegExp(`<p[^>]*>\\s*${pageMarkerRegex.source}\\s*</p>`, 'gi'), '');
            processedHtml = processedHtml.replace(pageMarkerRegex, '');
            processedHtml = processedHtml.replace(/<p[^>]*>\s*(?:&nbsp;|\s)*<\/p>/gi, '');
            processedHtml = processedHtml.replace(/<p[^>]*>\s*P\s+R\s+E\s+S\s+E\s+N\s+T\s*<\/p>/gi, '<h3>PRESENT</h3>');
            processedHtml = processedHtml.replace(/<p[^>]*>\s*R\s+E\s+A\s+D\s+I\s+N\s+G\s*<\/p>/gi, '<h3 id="reading-start-header">READING</h3>');
            setContent(parseCitationsInHtml(processedHtml));
        } else {
            setContent("An error occurred while loading content.");
        }
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, [file]);

  useEffect(() => {
    // Step 0: Always restore previous highlights first to ensure no lingering/stale states
    restorePermanentHTMLs();

    if (highlightText && contentRef.current && !loading) {
      const normalizedHighlight = highlightText.replace(/\s+/g, ' ').trim();
      
      // Step 1: Find all potential content blocks
      const elements = Array.from(contentRef.current.querySelectorAll('p, li, h1, h2, h3, h4, blockquote')) as HTMLElement[];
      const leafElements = elements.filter(el => !el.querySelector('p, li, h1, h2, h3, h4, blockquote'));

      // Step 2: Find the starting element
      const firstSection = normalizedHighlight.length > 80 
        ? normalizedHighlight.substring(0, 80).trim() 
        : normalizedHighlight;

      let startIndex = -1;
      const isParaBookmark = /^\s*[\(\[]?\d+[\)\]\.]?\s*$/.test(normalizedHighlight);
      for (let i = 0; i < leafElements.length; i++) {
        const el = leafElements[i];
        const textContent = el.textContent || '';
        const normalizedContent = textContent.replace(/\s+/g, ' ').trim();
        if (!normalizedContent) continue;

        if (normalizedContent === normalizedHighlight || 
            (isParaBookmark && normalizedContent.startsWith(normalizedHighlight)) ||
            (normalizedContent.startsWith(normalizedHighlight) && normalizedHighlight.length > 5) ||
            (normalizedHighlight.startsWith(normalizedContent) && normalizedContent.length > 15) ||
            (normalizedContent.includes(firstSection) && firstSection.length > 20)) {
          startIndex = i;
          break;
        }
      }

      // Helper to wrap the matching text segment inside the leaf element in standard inline permanent-highlight span
      const highlightTextInElement = (el: HTMLElement, searchText: string) => {
        if (!searchText) return;
        
        // Save original HTML if not already saved
        if (!permanentHTMLsRef.current.has(el)) {
          permanentHTMLsRef.current.set(el, el.innerHTML);
        }
        
        const textNodes: { node: Text; start: number; end: number }[] = [];
        let currentPos = 0;
        
        const findTextNodes = (node: Node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent || '';
            textNodes.push({
              node: node as Text,
              start: currentPos,
              end: currentPos + text.length
            });
            currentPos += text.length;
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            if (element.classList.contains('paragraph-bookmark-btn') || element.classList.contains('permanent-highlight')) {
              return;
            }
            for (let j = 0; j < node.childNodes.length; j++) {
              findTextNodes(node.childNodes[j]);
            }
          }
        };
        
        findTextNodes(el);
        
        const fullText = textNodes.map(tn => tn.node.textContent).join('');
        const normFullText = fullText.toLowerCase().replace(/\s+/g, ' ');
        const normSearchText = searchText.toLowerCase().replace(/\s+/g, ' ');
        
        let matchIndex = normFullText.indexOf(normSearchText);
        let matchedLength = normSearchText.length;
        
        if (matchIndex === -1) {
          // Slide window fallback to handle multi-paragraph highlight overlaps
          let bestMatchIndex = -1;
          let bestLen = 0;
          
          for (let len = Math.min(normSearchText.length, normFullText.length); len >= 10; len--) {
            // Try matching start of searchText
            const idxStart = normFullText.indexOf(normSearchText.substring(0, len));
            if (idxStart !== -1) {
              bestMatchIndex = idxStart;
              bestLen = len;
              break;
            }
            // Try matching end of searchText
            const idxEnd = normFullText.indexOf(normSearchText.substring(normSearchText.length - len));
            if (idxEnd !== -1) {
              bestMatchIndex = idxEnd;
              bestLen = len;
              break;
            }
          }
          
          if (bestMatchIndex !== -1) {
            matchIndex = bestMatchIndex;
            matchedLength = bestLen;
          } else {
            return;
          }
        }
        
        // Map normalized indices to raw fullText indices
        const normToOrig: number[] = [];
        for (let j = 0; j < fullText.length; j++) {
          const char = fullText[j];
          normToOrig.push(j);
        }
        
        const startOrigIndex = normToOrig[Math.min(matchIndex, normToOrig.length - 1)];
        const endOrigIndex = normToOrig[Math.min(matchIndex + matchedLength, normToOrig.length - 1)] + 1;
        
        const affectedNodes: { node: Text; startOffset: number; endOffset: number }[] = [];
        for (const tn of textNodes) {
          if (tn.end > startOrigIndex && tn.start < endOrigIndex) {
            const nodeStart = Math.max(tn.start, startOrigIndex);
            const nodeEnd = Math.min(tn.end, endOrigIndex);
            affectedNodes.push({
              node: tn.node,
              startOffset: nodeStart - tn.start,
              endOffset: nodeEnd - tn.start
            });
          }
        }
        
        for (let m = affectedNodes.length - 1; m >= 0; m--) {
          const { node, startOffset, endOffset } = affectedNodes[m];
          const text = node.textContent || '';
          const beforeText = text.substring(0, startOffset);
          const matchText = text.substring(startOffset, endOffset);
          const afterText = text.substring(endOffset);
          
          const fragment = document.createDocumentFragment();
          if (beforeText) {
            fragment.appendChild(document.createTextNode(beforeText));
          }
          if (matchText) {
            const span = document.createElement('span');
            span.className = 'permanent-highlight';
            span.textContent = matchText;
            fragment.appendChild(span);
          }
          if (afterText) {
            fragment.appendChild(document.createTextNode(afterText));
          }
          node.parentNode?.replaceChild(fragment, node);
        }
      };

      // Step 3: Highlight the entire range if start was found
      if (startIndex !== -1) {
        let currentTextMatch = "";
        leafElements[startIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        for (let i = startIndex; i < leafElements.length; i++) {
          const el = leafElements[i];
          const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
          if (!text) continue;

          // Instead of adding class to the parent block element, we nest the highlights inside the element!
          highlightTextInElement(el, normalizedHighlight);
          if (el.querySelector('.permanent-highlight')) {
              el.classList.add('has-permanent-highlight');
          }
          
          // Accumulate text to see if we've covered the full highlight
          currentTextMatch += (currentTextMatch ? " " : "") + text;
          
          // Stop if we've reached the end of the provided highlight text
          if (currentTextMatch.length >= normalizedHighlight.length * 0.95 || 
              (i > startIndex && text.match(/^(\(\d+\)|\d+\.|\d+\))/))) {
            break;
          }
        }
        setPermanentlyHighlightedEl(leafElements[startIndex]);
      }
    }
  }, [highlightText, loading, content]);

  useEffect(() => {
    const contentDiv = contentRef.current;
    if (contentDiv && permanentlyHighlightedEl) {
      const clearVirtualSelection = () => {
        setPermanentlyHighlightedEl(null);
      };
      contentDiv.addEventListener('click', clearVirtualSelection, { once: true });
      return () => {
        contentDiv.removeEventListener('click', clearVirtualSelection);
      };
    }
  }, [permanentlyHighlightedEl]);

  const restoreOriginalHTMLs = () => {
    if (highlightedWordRef.current) {
        highlightedWordRef.current.classList.remove('tts-word-highlight');
        highlightedWordRef.current = null;
    }
    originalHTMLsRef.current.forEach((html, el) => {
        el.innerHTML = html;
    });
    originalHTMLsRef.current.clear();
  };

  const stopSpeaking = () => {
    stopSpeakingRef.current = true;
    window.speechSynthesis.cancel();
    restoreOriginalHTMLs();
    setIsSpeaking(false);
  };

  const getBestVoice = () => {
      if (selectedVoiceURI) {
        const selected = voices.find(v => v.voiceURI === selectedVoiceURI);
        if (selected) return selected;
      }
      
      let currentLang = document.documentElement.lang || 'en';
      const match = document.cookie.match(/googtrans=\/auto\/([a-zA-Z-]+)/);
      if (match && match[1]) {
          currentLang = match[1];
      }

      const matchingVoices = voices.filter(v => v.lang.startsWith(currentLang.split('-')[0]));
      matchingVoices.sort((a, b) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          const scoreA = (aName.includes('microsoft') ? 2 : 0) + (aName.includes('natural') ? 1 : 0) + (aName.includes('online') ? 0.5 : 0);
          const scoreB = (bName.includes('microsoft') ? 2 : 0) + (bName.includes('natural') ? 1 : 0) + (bName.includes('online') ? 0.5 : 0);
          return scoreB - scoreA;
      });
      return matchingVoices.length > 0 ? matchingVoices[0] : null;
  };

  const normalizeTextForSpeech = (text: string): string => {
    if (!text) return '';
    // 1. Fix reading numbers: 0065-0003 -> 65-3
    // Matches patterns like 0001-0002 or 0123-4567
    let normalized = text.replace(/\b0+(\d+)-0+(\d+)\b/g, '$1-$2');
    normalized = normalized.replace(/\b0+([1-9]\d*)\b/g, '$1'); // 007 -> 7
    
    // 2. Fix all-caps words (2+ chars) to lowercase to prevent spelling out (e.g. "IN", "THE")
    normalized = normalized.replace(/\b([A-Z]{2,})\b/g, (match) => {
        return match.toLowerCase();
    });
    return normalized;
  };

  const speakParagraphSequence = (elements: HTMLElement[], index: number, startCharOffset = 0) => {
    if (stopSpeakingRef.current || index >= elements.length) {
        stopSpeaking();
        return;
    }

    const element = elements[index];
    // Use textContent to get the normalized text we set in wrapWordsInSpans
    const text = element.textContent?.trim() || "";

    if (!text) {
        speakParagraphSequence(elements, index + 1, 0);
        return;
    }
    
    const textToSpeak = (startCharOffset > 0 && startCharOffset < text.length)
        ? text.substring(startCharOffset)
        : text;

    const spanMap: {span: HTMLElement, start: number, end: number}[] = [];
    let currentIndex = 0;
    
    function buildMap(node: Node) {
        for (const child of Array.from(node.childNodes)) {
            if (child.nodeType === Node.ELEMENT_NODE) {
                const el = child as HTMLElement;
                if (el.classList.contains('tts-word-wrapper')) {
                    const wordText = el.textContent || '';
                    spanMap.push({ span: el, start: currentIndex, end: currentIndex + wordText.length });
                    currentIndex += wordText.length;
                } else {
                    buildMap(el); // Recurse into non-wrapper elements
                }
            } else if (child.nodeType === Node.TEXT_NODE) {
                currentIndex += (child.textContent || '').length;
            }
        }
    }
    buildMap(element);

    if (spanMap.length === 0) {
        speakParagraphSequence(elements, index + 1, 0);
        return;
    }

    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    const voice = getBestVoice();
    if (voice) utterance.voice = voice;
    utterance.lang = document.documentElement.lang || 'en';
    utterance.rate = speechRate;

    utterance.onboundary = (event) => {
        if (event.name !== 'word' || stopSpeakingRef.current) return;
        
        const adjustedCharIndex = event.charIndex + startCharOffset;
        
        let targetSpan: HTMLElement | null = null;
        for(const item of spanMap) {
            if (adjustedCharIndex >= item.start) {
                targetSpan = item.span;
            }
            if (adjustedCharIndex < item.end) {
                break;
            }
        }
        
        if (targetSpan && highlightedWordRef.current !== targetSpan) {
            if (highlightedWordRef.current) {
                highlightedWordRef.current.classList.remove('tts-word-highlight');
            }
            targetSpan.classList.add('tts-word-highlight');
            highlightedWordRef.current = targetSpan;

            const rect = targetSpan.getBoundingClientRect();
            if (rect.top < 0 || rect.bottom > window.innerHeight) {
                targetSpan.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            }
        }
    };

    utterance.onend = () => {
        if (highlightedWordRef.current) {
            highlightedWordRef.current.classList.remove('tts-word-highlight');
            highlightedWordRef.current = null;
        }
        if (!stopSpeakingRef.current) {
            speakParagraphSequence(elements, index + 1, 0);
        }
    };
    
    utterance.onerror = (e) => {
        // Log as a warning instead of a fatal error since speech synthesis is commonly blocked/unsupported in sandboxed, headless, or iframe environments.
        console.warn("TTS Speech Synthesis warning (standard in sandboxed/headless environments):", (e as any).error || e);
        if (!stopSpeakingRef.current) {
            speakParagraphSequence(elements, index + 1, 0);
        }
    };

    window.speechSynthesis.speak(utterance);
  };

  const wrapWordsInSpans = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
        if (!node.textContent?.trim()) return;
        const fragment = document.createDocumentFragment();
        const words = node.textContent.split(/(\s+)/);
        words.forEach(word => {
            if (word.trim()) {
                const span = document.createElement('span');
                span.className = 'tts-word-wrapper';
                
                const normalized = normalizeTextForSpeech(word);
                span.textContent = normalized;
                
                // If we normalized an all-caps word, use CSS to keep it looking uppercase
                if (word !== normalized && word === word.toUpperCase() && word.length > 1) {
                    span.style.textTransform = 'uppercase';
                }
                
                fragment.appendChild(span);
            } else {
                fragment.appendChild(document.createTextNode(word));
            }
        });
        node.parentNode?.replaceChild(fragment, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
        [...node.childNodes].forEach(wrapWordsInSpans);
    }
  };

  const handleSpeak = () => {
      if (isSpeaking) {
          stopSpeaking();
      } else {
          if (!contentRef.current) return;
          notifyTTSStarted();
          
          const speakableElements = Array.from(
              contentRef.current.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, blockquote')
          ) as HTMLElement[];
          
          const filteredElements = speakableElements.filter(el => el.textContent?.trim());
          
          if (filteredElements.length === 0) return;

          setIsSpeaking(true);
          stopSpeakingRef.current = false;

          filteredElements.forEach(el => {
              if(!originalHTMLsRef.current.has(el)) {
                  originalHTMLsRef.current.set(el, el.innerHTML);
              }
              wrapWordsInSpans(el);
          });
          
          let startIndex = 0;
          let startCharOffset = 0;
          let startIndexFound = false;
          let startNodeForSearch: Node | null = null;
          const selection = window.getSelection();

          if (hasSelection && selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              startNodeForSearch = range.startContainer;
              
              let parentBlock: HTMLElement | null = null;
              let currentNode: Node | null = startNodeForSearch;
              
              while (currentNode && currentNode !== contentRef.current) {
                  const foundIndex = filteredElements.findIndex(el => el === currentNode);
                  if (foundIndex !== -1) {
                      parentBlock = filteredElements[foundIndex];
                      startIndex = foundIndex;
                      startIndexFound = true;
                      break;
                  }
                  currentNode = currentNode.parentNode;
              }

              if (parentBlock) {
                  const treeWalker = document.createTreeWalker(parentBlock, NodeFilter.SHOW_TEXT);
                  let accumulatedOffset = 0;
                  let foundStartNode = false;
                  while (treeWalker.nextNode()) {
                      const currentTextNode = treeWalker.currentNode;
                      if (currentTextNode === range.startContainer) {
                          accumulatedOffset += range.startOffset;
                          foundStartNode = true;
                          break;
                      }
                      accumulatedOffset += currentTextNode.textContent?.length || 0;
                  }
                  if (foundStartNode) {
                      startCharOffset = accumulatedOffset;
                  }
              }
          } 
          else if (permanentlyHighlightedEl) {
              startNodeForSearch = permanentlyHighlightedEl;
          }
          
          if (startNodeForSearch && !startIndexFound) {
              let currentNode: Node | null = startNodeForSearch;
              while (currentNode && currentNode !== contentRef.current) {
                  const foundIndex = filteredElements.findIndex(el => el === currentNode);
                  if (foundIndex !== -1) {
                      startIndex = foundIndex;
                      startIndexFound = true;
                      break;
                  }
                  currentNode = currentNode.parentNode;
              }
          }

          if (!startIndexFound) {
              const readingHeader = contentRef.current?.querySelector('#reading-start-header');
              if (readingHeader) {
                  const headerIndex = filteredElements.findIndex(el => el === readingHeader);
                  if (headerIndex !== -1 && headerIndex + 1 < filteredElements.length) {
                      startIndex = headerIndex + 1;
                  }
              }
          }
          
          speakParagraphSequence(filteredElements, startIndex, startCharOffset);
      }
  };

  const handleOpenBookmarkDialog = (paragraphId?: string, paragraphText?: string) => {
    const title = paragraphId ? `${file.name} - ${paragraphId}` : file.name;
    onOpenBookmarkDialog(title, file.id, file.name, paragraphId, paragraphText);
  };

  useEffect(() => {
    if (!loading && contentRef.current) {
      // Find all potential content blocks including standard paragraphs and headers
      const elements = Array.from(contentRef.current.querySelectorAll('p, li, h1, h2, h3, h4, blockquote, .reading-paragraph')) as HTMLElement[];
      
      elements.forEach((el, index) => {
        // Skip elements that are just containers for other elements we've already selected
        if (el.querySelector('p, li, h1, h2, h3, h4, blockquote')) return;

        const text = el.textContent?.trim() || '';
        // Matches (1), 1., 1) at the start of the line - specifically looking for section numbering
        const match = text.match(/^(\(\d+\)|\d+\.|\d+\))/);
        
        if (match) {
          const paragraphId = match[0];
          const bookmarked = bookmarks?.some(b => b.fileId === file.id && b.paragraphId === paragraphId);
          let btn = el.querySelector('.paragraph-bookmark-btn') as HTMLButtonElement;

          if (!btn) {
            btn = document.createElement('button');
            btn.innerHTML = '<i class="fa-solid fa-bookmark text-[10px]"></i>';
            btn.title = 'Bookmark this section';
            
            btn.onclick = (e) => {
              e.stopPropagation();
              e.preventDefault();
              
              // NEW: If already bookmarked, remove it directly
              const existing = bookmarks?.find(b => b.fileId === file.id && b.paragraphId === paragraphId);
              if (existing) {
                  onDeleteBookmark(existing.id);
                  return;
              }

              // We want to capture the "unit" of text from this number until the next number or EOF
              let wrapperText = text;
              
              // Look ahead in the elements list to grab subsequent paragraphs that are part of this section
              for (let i = index + 1; i < elements.length; i++) {
                const nextEl = elements[i];
                // Skip container elements to avoid double-counting
                if (nextEl.querySelector('p, li, h1, h2, h3, h4, blockquote')) continue;

                const nextText = nextEl.textContent?.trim() || '';
                if (!nextText) continue;

                // Stop if we hit a new numbered section
                if (nextText.match(/^(\(\d+\)|\d+\.|\d+\))/)) break;
                
                // Otherwise, this is a continuation paragraph - clean and add it
                const cleaned = nextText.replace(/\b\d+-\d+\s+Page\s+\d+\b/gi, '').trim();
                if (cleaned) {
                  wrapperText += '\n\n' + cleaned;
                }
              }

              handleOpenBookmarkDialog(paragraphId, wrapperText);
            };
            
            el.prepend(btn);
            el.classList.add('has-bookmark-btn', 'relative', 'pl-8');
          }

          if (bookmarked) {
              el.classList.add('bookmarked-section', 'bg-amber-50/30', 'dark:bg-amber-900/10', 'rounded-lg');
          } else {
              el.classList.remove('bookmarked-section', 'bg-amber-50/30', 'dark:bg-amber-900/10', 'rounded-lg');
          }

          btn.className = `paragraph-bookmark-btn absolute left-0 top-0 mt-0.5 transition-colors inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 flex-none align-middle pointer-events-auto ${bookmarked ? 'text-amber-500 scale-110' : 'text-stone-300 hover:text-amber-500 hover:scale-110'}`;
        }
      });
    }
  }, [loading, content, bookmarks]);

  const isReadingBookmarked = bookmarks?.some(b => b.fileId === file.id && !b.paragraphId);

  const headerActions = (
    <div className="flex items-center gap-2">
        <button 
            onClick={() => {
                const existing = bookmarks?.find(b => b.fileId === file.id && !b.paragraphId);
                if (existing) {
                    onDeleteBookmark(existing.id);
                } else {
                    handleOpenBookmarkDialog();
                }
            }}
            className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${isReadingBookmarked ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 shadow-inner ring-1 ring-amber-500/20' : 'bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:text-amber-500 dark:hover:text-amber-400'}`}
            title={isReadingBookmarked ? "Remove bookmark" : "Bookmark this reading"}
        >
            <i className={`fa-solid ${isReadingBookmarked ? 'fa-bookmark' : 'fa-bookmark opacity-40'}`}></i>
        </button>
        <button 
            onClick={handleSpeak}
            className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${isSpeaking ? 'bg-amber-100 text-amber-500 animate-pulse' : 'bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200'}`}
            title={isSpeaking ? "Stop Reading" : (hasSelection || permanentlyHighlightedEl ? "Start from selected text" : "Read Aloud")}
        >
            <i className={`fa-solid ${isSpeaking ? 'fa-stop' : 'fa-volume-high'}`}></i>
        </button>
        <div ref={ttsSettingsRef} className="relative">
            <button 
                onClick={() => setIsTTSSettingsOpen(!isTTSSettingsOpen)}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${isTTSSettingsOpen ? 'bg-amber-100 text-amber-500' : 'bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200'}`}
                title="Speech Settings"
            >
                <i className="fa-solid fa-sliders"></i>
            </button>
            {isTTSSettingsOpen && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-white dark:bg-stone-800 rounded-lg shadow-xl border border-stone-200 dark:border-stone-700 z-50 p-4 animate-in fade-in zoom-in-95 duration-200">
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs font-bold text-stone-400 uppercase tracking-wider">Voice</label>
                            <select
                                value={selectedVoiceURI || ''}
                                onChange={(e) => setSelectedVoiceURI(e.target.value)}
                                className="w-full mt-1 p-2 border border-stone-200 dark:border-stone-600 bg-white dark:bg-stone-700 rounded-md text-sm text-stone-800 dark:text-stone-200 custom-scrollbar"
                            >
                                <option value="">Auto-select</option>
                                {voices.map(voice => (
                                    <option key={voice.voiceURI} value={voice.voiceURI}>{voice.name} ({voice.lang})</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-stone-400 uppercase tracking-wider">Speed ({speechRate}x)</label>
                            <input 
                                type="range"
                                min="0.5"
                                max="2"
                                step="0.1"
                                value={speechRate}
                                onChange={(e) => setSpeechRate(parseFloat(e.target.value))}
                                className="w-full mt-2 accent-amber-500"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-[#fbfaf6] dark:bg-stone-900 transition-colors duration-300">
      <WikiHeader 
        title={file.name}
        onBack={onBack}
        onMenuClick={onMenuClick}
        isSidebarOpen={isSidebarOpen}
        breadcrumbRootLabel={breadcrumbRootLabel || (activePage === 'readings' ? "Archive" : "Wiki")}
        showSearch={showSearch}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        rightAction={!loading ? headerActions : undefined}
      />
      <div className="flex-1 flex overflow-hidden relative">
        {sidebar}
        <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar bg-white dark:bg-stone-900 shadow-inner">
            <div className="p-6 md:p-12 pb-20">
               <div className="max-w-4xl mx-auto">
                 {file.imageUrl && (
                     <div className="mb-10 rounded-2xl overflow-hidden h-64 md:h-[400px] shadow-xl border border-stone-100 dark:border-stone-800">
                        <img src={getResizedImageUrl(file.imageUrl, '1800x1800')} alt={file.name} className="w-full h-full object-cover" />
                     </div>
                 )}
                 <h1 className="text-4xl md:text-6xl font-serif font-bold text-stone-900 dark:text-stone-100 mb-6 border-b border-stone-100 dark:border-stone-800 pb-4">
                    {file.name}
                 </h1>
                 <div className="flex items-center gap-4 mb-10 text-xs font-bold uppercase tracking-widest text-stone-400">
                    <span className="bg-stone-100 dark:bg-stone-800 px-3 py-1 rounded-full text-amber-600">{file.category}</span>
                    <span>{file.lastModified}</span>
                 </div>
                 
                 {loading ? (
                     <div className="flex flex-col items-center justify-center py-20 gap-4">
                        <i className="fa-solid fa-circle-notch animate-spin text-3xl text-amber-500"></i>
                        <span className="text-stone-400 text-xs font-bold uppercase tracking-widest">Unrolling Scroll...</span>
                     </div>
                 ) : (
                     <ArticleContent ref={contentRef} content={content} />
                 )}
               </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default ReadingView;
